import { compareR2Group } from "./r2-compare-data.js";

const config = window.FIPAV_PUBLIC_CONFIG || {};

export const runtime = {
  legacyOrigin: import.meta.env.DEV ? "/_legacy" : (config.legacyOrigin || "https://fipavonline.it").replace(/\/$/, ""),
  apiOrigin: import.meta.env.DEV ? "/_api" : String(config.apiOrigin || "https://app.fipavonline.it").replace(/\/$/, ""),
  r2Origin: String(config.r2Origin || "https://data.fipavonline.it").replace(/\/$/, ""),
  refreshMs: Math.max(Number(config.refreshMs) || 60000, 10000),
  mapkitToken: config.mapkitTokens?.[window.location.origin] || config.mapkitToken || "",
};

async function fetchJson(url, signal) {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "omit",
    signal,
  });
  if (!response.ok) throw new Error(`Servizio dati: HTTP ${response.status}`);
  return response.json();
}

async function fetchJsonWithFallback(primaryUrl, fallbackUrl, signal) {
  try {
    return await fetchJson(primaryUrl, signal);
  } catch (error) {
    if (error?.name === "AbortError" || signal?.aborted) throw error;
    return fetchJson(fallbackUrl, signal);
  }
}

let committeeProfiles;

function normalizeCommitteeLogo(value) {
  if (!value) return "";
  try {
    const source = new URL(String(value), "https://fipavonline.it");
    if (source.pathname.startsWith("/img/loghi/") &&
        ["fipavonline.it", "www.fipavonline.it", "app.fipavonline.it", "localhost", "127.0.0.1", "::1", "[::1]"].includes(source.hostname)) {
      // Cambia solo l'origine: percorso e query dei JSON statici restano intatti.
      return new URL(`${source.pathname}${source.search}${source.hash}`, `${runtime.r2Origin}/`).href;
    }
    if (["localhost", "127.0.0.1", "::1"].includes(source.hostname)) {
      return new URL(`${source.pathname}${source.search}`, "https://fipavonline.it").href;
    }
    if (source.protocol === "http:") source.protocol = "https:";
    return source.href;
  } catch (_) {
    return "";
  }
}

export function normalizeCommitteeProfile(profile, fallbackUrl = "") {
  return {
    ...profile,
    image: normalizeCommitteeLogo(profile.image),
    url: profile.website || profile.url || fallbackUrl,
  };
}

async function getCommitteeProfiles(signal) {
  if (!committeeProfiles) {
    let payload;
    try {
      payload = await fetchJson(`${runtime.apiOrigin}/api/v2/commettees`, signal);
    } catch (error) {
      if (!import.meta.env.DEV || error?.name === "AbortError") throw error;
      payload = await fetchJson("/data/committee-profiles.json", signal);
    }
    const directory = await fetchJson("/data/committees.json", signal);
    const committeeUrls = new Map(
      Object.values(directory?.regioni || {})
        .flatMap((region) => region.comitati || [])
        .filter((committee) => committee.id && committee.url)
        .map((committee) => [String(committee.id), String(committee.url)]),
    );
    const profiles = payload?.data?.commettees?.others || [];
    committeeProfiles = new Map(profiles.map((profile) => [String(profile.id),
      normalizeCommitteeProfile(profile, committeeUrls.get(String(profile.id)) || ""),
    ]));
  }
  return committeeProfiles;
}

export const api = {
  committees: (signal) => fetchJson("/data/committees.json", signal),
  committeeProfile: async (committee, signal) => {
    try {
      // Stessa fonte di Gare di oggi: indirizzo, contatti e logo uniformi.
      // Non memorizzare il profilo R2 per tutta la sessione della SPA.
      const payload = await api.live(committee, signal);
      const profile = payload?.data?.commettees?.["my-commettee"];
      if (profile) return normalizeCommitteeProfile(profile);
    } catch (error) {
      if (error?.name === "AbortError" || signal?.aborted) throw error;
    }
    // Alcuni comitati non hanno ancora il JSON delle gare sul disco/R2.
    const profiles = await getCommitteeProfiles(signal);
    return profiles.get(String(committee)) || null;
  },
  live: (committee, signal) => fetchJson(
    `${runtime.r2Origin}/public/cache/getGironiComiOggiv2_${encodeURIComponent(committee)}.json`, signal,
  ),
  posters: (committee, signal) => fetchJson(
    `${runtime.r2Origin}/public/locandine/${encodeURIComponent(committee)}.json`, signal,
  ),
  groups: (committee, signal) => fetchJsonWithFallback(
    `${runtime.r2Origin}/api/v2/calendar/${encodeURIComponent(committee)}/0`,
    `${runtime.apiOrigin}/api/v2/calendar/${encodeURIComponent(committee)}/0`,
    signal,
  ),
  calendar: (_committee, group, signal) => fetchJsonWithFallback(
    `${runtime.r2Origin}/api/v2/calendar/0/${encodeURIComponent(group)}`,
    `${runtime.apiOrigin}/api/v2/calendar/0/${encodeURIComponent(group)}`,
    signal,
  ),
  standings: (group, signal) => fetchJsonWithFallback(
    `${runtime.r2Origin}/api/v2/tables/${encodeURIComponent(group)}`,
    `${runtime.apiOrigin}/api/v2/tables/${encodeURIComponent(group)}`,
    signal,
  ),
  r2Compare: (group, signal) => compareR2Group(group, runtime.apiOrigin, runtime.r2Origin, signal),
};
